/**
 * Firebase Configuration (Client SDK)
 * Frontend Firebase configuration for authentication and Firestore
 */

import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  connectFirestoreEmulator,
  getFirestore
} from 'firebase/firestore';
import logger from '../utils/logger.js';

/**
 * Firebase configuration from environment variables
 */
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// 🔐 AUTH DOMAIN DEBUG - Log to verify production uses getfinlist.com
console.log('🔐 Firebase authDomain:', firebaseConfig.authDomain);

/**
 * Validate Firebase configuration
 */
const validateConfig = () => {
  const requiredKeys = [
    'VITE_FIREBASE_API_KEY',
    'VITE_FIREBASE_AUTH_DOMAIN', 
    'VITE_FIREBASE_PROJECT_ID',
    'VITE_FIREBASE_STORAGE_BUCKET',
    'VITE_FIREBASE_MESSAGING_SENDER_ID',
    'VITE_FIREBASE_APP_ID'
  ];

  const missingKeys = requiredKeys.filter(key => !import.meta.env[key]);
  
  if (missingKeys.length > 0) {
    logger.error('Missing Firebase configuration:', missingKeys);
    throw new Error(`Missing Firebase environment variables: ${missingKeys.join(', ')}`);
  }

  logger.debug('✅ Firebase configuration validated');
};

// Validate configuration
validateConfig();

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase Authentication
export const auth = getAuth(app);

// Initialize Firestore with modern persistence (offline-first, multi-tab safe)
let db;
try {
  db = initializeFirestore(app, {
    localCache: persistentLocalCache({
      tabManager: persistentMultipleTabManager(),
    }),
  });
  logger.debug('✅ Firestore configured with persistent local cache (multi-tab)');
} catch (err) {
  logger.warn('⚠️ Falling back to default Firestore (persistence init failed)', err?.message || err);
  db = getFirestore(app);
}

export { db };

const globalScope = typeof globalThis !== 'undefined' ? globalThis : window;

// Connect to emulators in development (must happen before enabling persistence)
// Check both DEV mode and VITE_USE_FIREBASE_EMULATOR flag
const useEmulator = import.meta.env.DEV && import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true';

if (useEmulator && globalScope) {
  if (!globalScope.__FIREBASE_EMULATORS_CONNECTED__) {
    // Commented out Auth Emulator to allow real Google sign-in
    // try {
    //   connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
    //   console.log('🔧 Connected to Firebase Auth Emulator');
    // } catch (error) {
    //   console.warn('Firebase Auth Emulator not running');
    // }

    try {
      connectFirestoreEmulator(db, 'localhost', 8080);
      logger.debug('🔧 Connected to Firestore Emulator');
    } catch (error) {
      logger.warn('Firestore Emulator not running');
    }

    globalScope.__FIREBASE_EMULATORS_CONNECTED__ = true;
  }
} else if (import.meta.env.DEV) {
  logger.debug('🔥 Using Production Firebase (Emulator disabled)');
}

// Persistence is configured via initializeFirestore above; no legacy enableIndexedDbPersistence required.

/**
 * Firebase Auth Providers
 */
import { GoogleAuthProvider, EmailAuthProvider } from 'firebase/auth';

export const googleProvider = new GoogleAuthProvider();
export const emailProvider = new EmailAuthProvider();

// Configure Google provider
googleProvider.addScope('profile');
googleProvider.addScope('email');
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

/**
 * Firestore collection references
 */
export const collections = {
  USERS: 'users',
  ITEMS: 'items',
  // AI & Financial Collections
  TRANSACTIONS: 'transactions',
  SHOPPING_LISTS: 'shopping_lists',
  CATEGORIES: 'categories',
  GOALS: 'savingsGoals',
  USER_INSIGHTS: 'user_insights'
};

/**
 * Firebase app instance
 */
export default app;

/**
 * Environment info
 */
export const firebaseEnv = {
  isDevelopment: import.meta.env.DEV,
  isProduction: import.meta.env.PROD,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN
};

// SECURITY FIX: Removed console.log that exposed Firebase config
// Use logger.debug() instead which is silent in production
logger.info('🔥 Firebase initialized');
logger.debug('Firebase environment:', {
  projectId: firebaseConfig.projectId,
  environment: import.meta.env.MODE
});
